import { useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, ClipboardCopy, RotateCcw, Info, AlertTriangle } from "lucide-react";

const GBP = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" });
const gbp = (n) => (Number.isFinite(n) ? GBP.format(n) : "—");
const num = (v) => {
  const x = Number.parseFloat(v);
  return Number.isFinite(x) && x >= 0 ? x : 0;
};

const DEFAULTS = {
  weeklyIncome: 900,
  weeklyCosts: 80,
  placementDays: 7,
  daytimeHours: 10,
  wakingNights: 0,
  nightAllowance: 65,
  roundTo: 5,
};

function Field({ id, label, hint, value, onChange, step = "1", min = "0", prefix, suffix }) {
  return (
    <label htmlFor={id} className="block">
      <span className="text-sm font-medium text-ink">{label}</span>
      {hint && <span className="block text-xs text-ink-muted mt-1 leading-snug">{hint}</span>}
      <div className="mt-2 flex items-stretch rounded-xl border border-slate-200 bg-white focus-within:border-purple focus-within:ring-2 focus-within:ring-purple/20 overflow-hidden">
        {prefix && <span className="px-3 flex items-center text-sm text-ink-muted bg-slate-50 border-r border-slate-200">{prefix}</span>}
        <input
          id={id}
          type="number"
          inputMode="decimal"
          min={min}
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`livein-input-${id}`}
          className="flex-1 px-3 py-2.5 text-sm bg-transparent focus:outline-none"
        />
        {suffix && <span className="px-3 flex items-center text-sm text-ink-muted bg-slate-50 border-l border-slate-200">{suffix}</span>}
      </div>
    </label>
  );
}

function Stat({ label, value, note, testid, big }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-5 ${big ? "border-purple bg-purple/5" : ""}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-ink-muted">{label}</div>
      <div className={`mt-2 font-display font-semibold text-ink ${big ? "text-3xl md:text-4xl text-purple" : "text-xl"}`}>{value}</div>
      {note && <div className="mt-2 text-xs text-ink-muted leading-relaxed">{note}</div>}
    </div>
  );
}

export default function LiveInRateCalculator() {
  const [f, setF] = useState(DEFAULTS);
  const printRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const reset = () => setF(DEFAULTS);

  const r = useMemo(() => {
    const weeklyIncome = num(f.weeklyIncome);
    const weeklyCosts = num(f.weeklyCosts);
    const placementDays = num(f.placementDays);
    const daytimeHours = num(f.daytimeHours);
    const wakingNights = num(f.wakingNights);
    const nightAllowance = num(f.nightAllowance);
    const roundTo = Math.max(num(f.roundTo), 0.01);
    if (placementDays <= 0) return { valid: false };
    const baseWeekly = weeklyIncome + weeklyCosts;
    const nightTotal = wakingNights * nightAllowance;
    const basePlacementAmount = (baseWeekly / 7) * placementDays;
    const preliminary = basePlacementAmount + nightTotal;
    const rawDayRate = preliminary / placementDays;
    const suggestedDayRate = Math.ceil(rawDayRate / roundTo) * roundTo;
    const placementQuote = suggestedDayRate * placementDays;
    const scheduledHours = daytimeHours * placementDays;
    return { valid: true, suggestedDayRate, baseWeekly, nightTotal, placementQuote, scheduledHours, placementDays, wakingNights };
  }, [f]);

  const summary = r.valid
    ? [
        "JCC Live-In Care Rate Starter",
        `Suggested day rate: ${gbp(r.suggestedDayRate)} per day`,
        `Base weekly amount before nights: ${gbp(r.baseWeekly)}`,
        `Waking-night allowance total: ${gbp(r.nightTotal)}`,
        `Total placement quote: ${gbp(r.placementQuote)} for ${r.placementDays.toFixed(0)} day${r.placementDays === 1 ? "" : "s"}`,
        `Planned daytime cover: ${r.scheduledHours.toFixed(1).replace(/\.0$/, "")} hours across the placement`,
        "",
        "Live-in care is not a 24-hour rate. Regular waking nights should be a separate arrangement.",
        "Planning guide only — not financial, legal or tax advice.",
      ].join("\n")
    : "";

  const onCopy = async () => {
    try { await navigator.clipboard.writeText(summary); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* clipboard not available */ }
  };
  const onPrint = () => window.print();

  return (
    <div className="min-h-screen bg-surface-carer" data-testid="page-livein-rate-calculator">
      <div className="container-jcc py-8 md:py-14 max-w-5xl">
        <Link to="/workspace/pro-carer-circle-hub" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink" data-testid="livein-back">
          <ArrowLeft className="w-4 h-4" /> Back to Pro Carer Office hub
        </Link>

        <header className="mt-6">
          <div className="overline text-purple">Pricing & Income Tools</div>
          <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl">Live-In Care Rate Starter</h1>
          <p className="mt-3 text-ink-muted max-w-2xl leading-relaxed">
            Price a live-in placement around active daytime cover, agreed breaks, rest, and a separate allowance for waking nights. Figures stay on your device.
          </p>
        </header>

        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/60 p-4 flex gap-3" data-testid="livein-not-24h">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" strokeWidth={1.75} />
          <p className="text-sm text-ink leading-relaxed">
            <span className="font-semibold">Live-in care is not a 24-hour rate.</span> A sustainable arrangement needs agreed breaks, uninterrupted rest, and — if regularly disturbed — a separate arrangement for waking nights.
          </p>
        </div>

        <div ref={printRef} className="mt-8 grid lg:grid-cols-5 gap-6">
          <form className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white p-6 md:p-8 space-y-5" onSubmit={(e)=>e.preventDefault()} data-testid="livein-form">
            <Field id="weeklyIncome" label="Weekly income target, before personal tax" hint="What you need to take home each week after business costs, before your own tax and NI." value={f.weeklyIncome} onChange={set("weeklyIncome")} prefix="£" />
            <Field id="weeklyCosts" label="Average weekly business costs" hint="Insurance, phone, admin, CPD, travel to/from placements — anything the placement doesn't cover." value={f.weeklyCosts} onChange={set("weeklyCosts")} prefix="£" />
            <Field id="placementDays" label="Days on this placement" hint="A typical live-in week is 5–7 days. Use the days you'll actually be on-site." value={f.placementDays} onChange={set("placementDays")} suffix="days" />
            <Field id="daytimeHours" label="Planned daytime care hours per day" hint="Active care hours you expect to work each day. Should allow proper breaks and daily rest." value={f.daytimeHours} onChange={set("daytimeHours")} suffix="hours" />
            <Field id="wakingNights" label="Expected waking nights per placement" hint="Nights when you're expected to be up. If it happens often, price these separately rather than absorb them into the day rate." value={f.wakingNights} onChange={set("wakingNights")} suffix="nights" />
            <Field id="nightAllowance" label="Allowance for each expected waking night" hint="A separate fee, on top of the day rate, for each waking-night shift." value={f.nightAllowance} onChange={set("nightAllowance")} prefix="£" />
            <Field id="roundTo" label="Round day rate up to" hint="Round up to the nearest £5 or £10 to keep the quote clean." step="1" value={f.roundTo} onChange={set("roundTo")} prefix="£" />
          </form>

          <aside className="lg:col-span-2 space-y-4">
            {!r.valid ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-ink-muted" data-testid="livein-empty">
                Enter at least one placement day to calculate your quote.
              </div>
            ) : (
              <>
                <Stat testid="livein-day-rate" big label="Suggested day rate" value={`${gbp(r.suggestedDayRate)} / day`} note="Covers your income target and business costs across the days you'll actually be on-site." />
                <div className="grid sm:grid-cols-2 gap-3">
                  <Stat testid="livein-base-weekly" label="Base weekly (before nights)" value={gbp(r.baseWeekly)} />
                  <Stat testid="livein-night-total" label="Waking-night allowance" value={gbp(r.nightTotal)} note="Priced separately from the day rate." />
                </div>
                <Stat testid="livein-quote" label="Total placement quote" value={gbp(r.placementQuote)} note={`Based on ${r.placementDays.toFixed(0)} day${r.placementDays === 1 ? "" : "s"} at ${gbp(r.suggestedDayRate)} per day.`} />
                <Stat testid="livein-scheduled" label="Planned daytime cover" value={`${r.scheduledHours.toFixed(1).replace(/\.0$/, "")} hours`} note="Total active-care hours across the placement, before agreed breaks and rest." />
              </>
            )}

            <div className="rounded-2xl bg-white border border-slate-200 p-4 flex flex-wrap gap-2">
              <button type="button" onClick={onPrint} className="btn-outline text-sm" data-testid="livein-print">
                <Printer className="w-4 h-4" strokeWidth={1.75} /> Print / save
              </button>
              <button type="button" onClick={onCopy} className="btn-outline text-sm" data-testid="livein-copy">
                <ClipboardCopy className="w-4 h-4" strokeWidth={1.75} /> {copied ? "Copied" : "Copy summary"}
              </button>
              <button type="button" onClick={reset} className="btn-outline text-sm" data-testid="livein-reset">
                <RotateCcw className="w-4 h-4" strokeWidth={1.75} /> Reset
              </button>
            </div>
          </aside>
        </div>

        <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50/40 p-5 flex gap-3" data-testid="livein-disclaimer">
          <Info className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" strokeWidth={1.75} />
          <p className="text-sm text-ink-muted leading-relaxed">
            Your suggested rate is a planning guide only. It is not financial, legal or tax advice. Your final rate should reflect your experience, responsibilities, location, insurance, expenses, working pattern and the individual care arrangement.
          </p>
        </div>
      </div>
    </div>
  );
}
